import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import Fuse from "fuse.js";
import { Observable, Subscription } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { FormDataOptions } from "src/app/models/models";

export type Ts1SelectManeuverModalData = {
	options: FormDataOptions[];
	usedCodes: string[];
};

@Component({
	selector: "app-ts1-select-maneuver-modal",
	templateUrl: "./ts1-select-maneuver-modal.component.html",
	styleUrls: ["./ts1-select-maneuver-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class Ts1SelectManeuverModalComponent implements OnInit, OnDestroy {
	selected: FormDataOptions | null = null;
	maneuverControl = new FormControl<FormDataOptions | string>("");
	filteredOptions!: Observable<FormDataOptions[]>;
	private fuse: Fuse<FormDataOptions> | null = null;
	private valueChangesSub?: Subscription;

	constructor(
		private dialogRef: MatDialogRef<Ts1SelectManeuverModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: Ts1SelectManeuverModalData,
	) {}

	ngOnInit(): void {
		const options = this.availableOptions;
		this.fuse = new Fuse(options, { keys: ["description", "code"], threshold: 0.4 });
		this.filteredOptions = this.maneuverControl.valueChanges.pipe(
			startWith(""),
			map((value) => this.filterOptions(value)),
		);
		this.valueChangesSub = this.maneuverControl.valueChanges.subscribe((value) => {
			this.syncSelectedFromControl(value);
		});
	}

	ngOnDestroy(): void {
		this.valueChangesSub?.unsubscribe();
	}

	get availableOptions(): FormDataOptions[] {
		const used = new Set(this.data?.usedCodes ?? []);
		return (this.data?.options ?? []).filter((o) => !used.has(o.code));
	}

	displayFn(option: FormDataOptions | string | null): string {
		if (!option) return "";
		if (typeof option === "string") return option;
		return option.description ?? "";
	}

	onOptionSelected(event: MatAutocompleteSelectedEvent): void {
		const value = event.option.value as FormDataOptions;
		this.selected = value;
		this.maneuverControl.setValue(value, { emitEvent: false });
	}

	clearSelection(): void {
		this.selected = null;
		this.maneuverControl.setValue("");
	}

	private syncSelectedFromControl(value: FormDataOptions | string | null): void {
		if (!value || typeof value === "string") {
			this.selected = null;
		}
	}

	private filterOptions(value: FormDataOptions | string | null): FormDataOptions[] {
		const options = this.availableOptions;
		if (!options.length) return [];

		const query = (typeof value === "string" ? value : (value?.description ?? "")).trim();
		if (!query) return options;

		if (this.fuse) {
			return this.fuse.search(query).map((r) => r.item);
		}

		const q = query.toLowerCase();
		return options.filter((o) => o.description?.toLowerCase().includes(q) || o.code?.toLowerCase().includes(q));
	}

	cancel(): void {
		this.dialogRef.close(null);
	}

	continue(): void {
		this.dialogRef.close(this.selected);
	}
}
